/**
 * Service Worker registration helper — PWA Phase 1b (May 7, 2026).
 *
 * Registers /sw.js after the app shell has loaded so SW install never blocks
 * first paint. Production-only by default; pass { force: true } to enable in
 * development for local PWA testing.
 *
 * See PWA_DESIGN.md §Implementation Phases (Phase 1b) and §Architectural
 * principle (the SW never caches medical content).
 */

const SW_URL = '/sw.js';

export function registerServiceWorker(options) {
  const force = options && options.force;
  // Only run in browsers that support it AND in production builds, unless
  // the caller explicitly opts in.
  if (!('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production' && !force) return;

  // Defer registration until the window 'load' event so the SW install does
  // not contend with first paint.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(SW_URL)
      .then((reg) => {
        if (process.env.NODE_ENV === 'production') {
          // Quiet in production; we have governance_events for telemetry.
          // eslint-disable-next-line no-console
          console.info('[SW] registered, scope:', reg.scope);
        }

        // When a new SW is waiting (updated build), nudge it to activate.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // New version ready; ask it to skip waiting so next reload picks it up.
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[SW] registration failed:', err && err.message);
      });
  });
}

/**
 * CS "Nuclear Option" — unregister the SW and force a clean reload.
 * Exposed for the customer support playbook (CUSTOMER_SERVICE_MANUAL.md).
 */
export async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return false;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(regs.map((r) => r.unregister()));
  return true;
}
